import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { Loader } from '../../ui/loader';

import * as actions from '../../../actions';
import { formatNumber, showMessage } from '../../Utils';
import { RootState } from '../../../types/redux';

// Code splitting
import Boards from './Boards';
import CustomContainer from '../../common/CustomContainer';
import HotArticles from '../common/HotArticles';
import UserDistributionsBarChart from '../../Charts/UserDistributionsBarChart';
import UserDistributionsMap from '../../Charts/UserDistributionsMap';

const MESSAGES = {
  SHOW_DB_STATS: `
    目前PTTBrain資料庫統計
  `,
  SHOW_USER_GEO_DISTRIBUTION: `
    根據文章與推噓回文的IP位置,
    統計鄉民在世界各城市的分佈情形
  `,
  SHOW_BOARDS: `
    目前資料庫所有的版
  `,
  SHOW_ARTICLES: `
    3天內統計全站熱門文章
  `,
};

const DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO = 1;
const HOT_ARTICLES_PAGE_SIZE = 5;
const TOTAL_HOT_ARTICLES_PAGES = 20;

const mapStateToProps = (state: RootState) => ({
  articles: state.metric.ptt.hotArticles,
  boards: state.board.all,
  dbStats: state.metric.ptt.dbStats,
  isMobile: state.browser.lessThan.medium,
  locations: state.metric.ptt.userGeoDistributions,
});

const connector = connect(mapStateToProps, actions);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

const Home: React.FC<Props> = ({
  articles,
  boards,
  dbStats,
  isMobile,
  locations,
  getBoards,
  getPttDBStats,
  getHotArticles,
  getUserGeoDistributions,
}) => {
  const fetchHotArticles = async (offset: number, sinceDaysAgo: number) => {
    await getHotArticles(sinceDaysAgo, HOT_ARTICLES_PAGE_SIZE, offset);
  };

  const fetchData = async () => {
    await Promise.all([
      getBoards(),
      getPttDBStats(),
      fetchHotArticles(
        articles.offset || 0,
        articles.sinceDaysAgo || DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO,
      ),
      getUserGeoDistributions(),
    ]);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleHotArticlesDaysAgoDropdown = (
    e: any,
    { value }: { value: number },
  ) => {
    fetchHotArticles(0, value);
  };

  const handleHotArticlesPagination = (
    e: any,
    { activePage }: { activePage: number },
  ) => {
    fetchHotArticles(
      (activePage - 1) * HOT_ARTICLES_PAGE_SIZE,
      DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO,
    );
  };

  return (
    <div>
      <CustomContainer>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className={isMobile ? 'text-xl' : 'text-2xl'}>
                目前PTT資料庫
              </CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-gray-500 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{MESSAGES.SHOW_DB_STATS}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardHeader>
          <CardContent>
            {dbStats.isFetching ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <div
                className={`grid grid-cols-3 gap-4 text-center ${
                  isMobile ? 'text-sm' : ''
                }`}
              >
                <div>
                  <p className="text-pink-600 font-semibold">文章數</p>
                  <p
                    className={`font-bold ${isMobile ? 'text-xl' : 'text-3xl'}`}
                  >
                    {formatNumber(dbStats.data.total_articles)}
                  </p>
                </div>
                <div>
                  <p className="text-teal-600 font-semibold">推噓回文數</p>
                  <p
                    className={`font-bold ${isMobile ? 'text-xl' : 'text-3xl'}`}
                  >
                    {formatNumber(dbStats.data.total_pushes)}
                  </p>
                </div>
                <div>
                  <p className="text-blue-600 font-semibold">ID 數</p>
                  <p
                    className={`font-bold ${isMobile ? 'text-xl' : 'text-3xl'}`}
                  >
                    {formatNumber(dbStats.data.total_users)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </CustomContainer>

      <CustomContainer>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className={isMobile ? 'text-xl' : 'text-2xl'}>
                所有板 ({boards.data.length})
              </CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-gray-500 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{MESSAGES.SHOW_BOARDS}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardHeader>
          <CardContent>
            {boards.isFetching ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <Boards data={boards.data} isMobile={isMobile} />
            )}
          </CardContent>
        </Card>
      </CustomContainer>

      <HotArticles
        isMobile={isMobile}
        isFetching={articles.isFetching}
        data={articles.data}
        offset={articles.offset || 0}
        total={TOTAL_HOT_ARTICLES_PAGES}
        pageSize={HOT_ARTICLES_PAGE_SIZE}
        daysAgo={articles.sinceDaysAgo || DEFAULT_HOT_ARTICLES_SINCE_DAYS_AGO}
        onPageChange={handleHotArticlesPagination}
        onChangeDaysAgoDropdown={handleHotArticlesDaysAgoDropdown}
      />
    </div>
  );
};

// @ts-ignore - TypeScript issue with React 18 and react-redux types
export default connector(Home) as any;

